<template>
	<div
		class="archive-app"
		:class="{ 'is-quartz': isQuartzArchive }"
		:style="{ '--product-type-color': productTypeColor }"
	>
		<div class="filter-bar">
			<div class="container-fluid">
				<div class="row">
					<div v-for="filter in dynamicFilters" :key="filter.taxonomy" class="col-md-4">
						<div class="filter-select-pill">
							<label class="form-label">{{ filter.label }}</label>
							<div class="search-box">
								<i class="bi bi-search"></i>
								<input
									v-model="filterSearchTerms[filter.taxonomy]"
									type="text"
									class="form-control"
									:placeholder="filter.placeholder"
									@focus="openAutocomplete(filter.taxonomy)"
									@blur="closeAutocomplete()"
									@keydown.enter.prevent="selectFirstSuggestion(filter.taxonomy)"
								/>

								<div
									class="filter-options"
									v-if="
										isAutocompleteOpen(filter.taxonomy) &&
										autocompleteOptions(filter.taxonomy).length
									"
								>
									<button
										v-for="term in autocompleteOptions(filter.taxonomy)"
										:key="`${filter.taxonomy}-${term.slug}`"
										type="button"
										class="filter-option-pill"
										@mousedown.prevent
										@click="addFilterTerm(filter.taxonomy, term.slug)"
									>
										{{ term.name }}
									</button>
								</div>
							</div>

							<div
								v-if="selectedTermsForTaxonomy(filter.taxonomy).length"
								class="selected-pills"
							>
								<span
									v-for="term in selectedTermsForTaxonomy(filter.taxonomy)"
									:key="`${filter.taxonomy}-${term.slug}-selected`"
									class="filter-tag"
								>
									{{ term.name }}
									<button
										@click="removeFilterTerm(filter.taxonomy, term.slug)"
										:aria-label="`Remove ${term.name} filter`"
									>
										<i class="bi bi-x"></i>
									</button>
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="results-info">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12">
						<p v-if="!loading" class="results-count">
							Showing {{ totalFilteredProducts }} of {{ totalProducts }} products
						</p>
					</div>
				</div>
			</div>
		</div>

		<div v-if="loading" class="loading-container">
			<div class="spinner-border" :style="{ color: productTypeColor }" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>

		<div v-else-if="error" class="error-container">
			<div class="alert alert-danger">
				<i class="bi bi-exclamation-triangle"></i>
				{{ error }}
			</div>
		</div>

		<div v-else class="products-container">
			<div class="container-fluid">
				<div v-if="!loading && seriesGroups.length === 0" class="no-results">
					<i class="bi bi-search"></i>
					<h3>No products found</h3>
					<p>Try adjusting your filters</p>
					<button @click="clearAllFilters" class="btn btn-primary">Clear Filters</button>
				</div>

				<div
					v-for="seriesGroup in seriesGroups"
					:key="seriesGroup.slug"
					class="series-group"
				>
					<div class="series-header">
						<h3 class="series-title">{{ seriesGroup.name }}</h3>
					</div>

					<transition name="fade" mode="out-in">
						<div
							v-if="viewMode === 'grid'"
							:key="`${seriesGroup.slug}-grid`"
							class="row g-4 products-grid"
						>
							<div
								v-for="product in seriesGroup.products"
								:key="product.id"
								:class="productGridColumnClass"
							>
								<ProductCard :product="product" @click="openProductModal" />
							</div>
						</div>

						<div v-else :key="`${seriesGroup.slug}-list`" class="products-list">
							<ProductListItem
								v-for="product in seriesGroup.products"
								:key="product.id"
								:product="product"
								@click="openProductModal"
							/>
						</div>
					</transition>
				</div>
			</div>
		</div>

		<ProductModal
			:product="selectedProduct"
			:isOpen="isProductModalOpen"
			:typeImageUrl="typeImageUrl"
			:productTypeColor="productTypeColor"
			@close="closeProductModal"
		/>

		<button
			v-if="showScrollTopButton"
			class="archive-scroll-top"
			@click="scrollToTop"
			aria-label="Scroll to top"
			title="Scroll to top"
		>
			<i class="bi bi-arrow-up"></i>
		</button>
	</div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import ProductCard from './ProductCard.vue';
import ProductListItem from './ProductListItem.vue';
import ProductModal from './ProductModal.vue';
import { useRoomvo } from '../composables/useRoomvo';

export default {
	name: 'ArchiveApp',
	components: {
		ProductCard,
		ProductListItem,
		ProductModal,
	},
	setup() {
		const allProducts = ref([]);
		const dynamicFilters = ref([]);
		const dynamicFilterTerms = ref({});
		const loading = ref(true);
		const error = ref(null);
		const totalProducts = ref(0);
		const viewMode = ref('grid');
		const selectedProduct = ref(null);
		const isProductModalOpen = ref(false);
		const isHydratingFromUrl = ref(true);
		const pendingDynamicParams = ref({});
		const showScrollTopButton = ref(false);
		const activeAutocompleteTaxonomy = ref(null);

		// Roomvo integration - refreshRoomvo rescans DOM after Vue finishes rendering
		const { refreshRoomvo } = useRoomvo();
		const filterSearchTerms = ref({});

		const filters = ref({
			dynamic: {},
		});

		const archiveAppElement = document.getElementById('archive-app');
		const postType = ref(archiveAppElement?.dataset.postType || 'ceramic');
		const typeImageUrl = ref(archiveAppElement?.dataset.typeImage || '');

		const productTypeColors = {
			ceramic: '#e1001f',
			rigid: '#f38a1f',
			lvt: '#fcb813',
			laminate: '#008f4c',
			wood: '#0a4280',
			carpet: '#a2228e',
			quartz: '#0092b0',
		};

		const productTypeColor = computed(() => {
			return productTypeColors[postType.value] || '#111';
		});

		const isQuartzArchive = computed(() => postType.value === 'quartz');

		const productGridColumnClass = computed(() => {
			if (isQuartzArchive.value) {
				return 'col-sm-12 col-md-6 col-lg-6';
			}

			return 'col-sm-6 col-md-4 col-lg-3';
		});

		const filteredProducts = computed(() => {
			return allProducts.value.filter(product => {
				for (const [taxonomy, selectedSlugs] of Object.entries(
					filters.value.dynamic || {}
				)) {
					if (!Array.isArray(selectedSlugs) || selectedSlugs.length === 0) {
						continue;
					}

					const productTerms = product.filter_terms?.[taxonomy] || [];
					const hasAnyMatch = selectedSlugs.some(slug => productTerms.includes(slug));
					if (!hasAnyMatch) {
						return false;
					}
				}

				return true;
			});
		});

		const seriesGroups = computed(() => {
			const groups = new Map();

			filteredProducts.value.forEach(product => {
				if (!product.series || !product.series_slug) {
					return;
				}

				const groupKey = product.series_slug;
				if (!groups.has(groupKey)) {
					groups.set(groupKey, {
						id: product.series_id || 0,
						name: product.series,
						slug: product.series_slug,
						products: [],
					});
				}

				groups.get(groupKey).products.push(product);
			});

			const sortedGroups = Array.from(groups.values()).sort((left, right) => {
				return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
			});

			sortedGroups.forEach(group => {
				group.products.sort((left, right) => {
					return left.title.localeCompare(right.title, undefined, {
						sensitivity: 'base',
					});
				});
			});

			return sortedGroups;
		});

		const totalFilteredProducts = computed(() => {
			return filteredProducts.value.length;
		});

		const fetchAllProducts = async () => {
			loading.value = true;
			error.value = null;

			try {
				const params = new URLSearchParams({
					action: 'get_archive_products_all',
					post_type: postType.value,
				});

				dynamicFilters.value.forEach(filter => {
					params.append('taxonomies[]', filter.taxonomy);
				});

				const response = await fetch(`${window.trendsData.ajaxUrl}?${params}`);

				if (!response.ok) {
					throw new Error('Failed to fetch products');
				}

				const data = await response.json();

				if (data.success) {
					allProducts.value = data.data.products || [];
					totalProducts.value = data.data.total;
				} else {
					throw new Error(data.data || 'Failed to fetch products');
				}
			} catch (err) {
				error.value = err.message;
				console.error('Error fetching products:', err);
			} finally {
				loading.value = false;
				// Trigger Roomvo to rescan for new buttons after Vue finishes rendering
				refreshRoomvo();
			}
		};

		const fetchFilterConfig = async () => {
			try {
				const params = new URLSearchParams({
					action: 'get_archive_filter_config',
					post_type: postType.value,
				});

				const response = await fetch(`${window.trendsData.ajaxUrl}?${params}`);

				if (!response.ok) {
					return;
				}

				const data = await response.json();

				if (data.success && data.data && data.data.length > 0) {
					dynamicFilters.value = data.data;
					dynamicFilters.value.forEach(filter => {
						fetchFilterTerms(filter.taxonomy);
					});
				}
			} catch (err) {
				console.error('Error fetching filter config:', err);
			}
		};

		const fetchFilterTerms = async taxonomy => {
			try {
				const params = new URLSearchParams({
					action: 'get_taxonomy_terms',
					taxonomy: taxonomy,
				});

				const response = await fetch(`${window.trendsData.ajaxUrl}?${params}`);

				if (!response.ok) {
					return;
				}

				const data = await response.json();

				if (data.success) {
					dynamicFilterTerms.value[taxonomy] = data.data;
				}
			} catch (err) {
				console.error(`Error fetching terms for ${taxonomy}:`, err);
			}
		};

		const initializeDynamicFilters = () => {
			const nextDynamic = {};
			dynamicFilters.value.forEach(filter => {
				const currentValue = filters.value.dynamic?.[filter.taxonomy];
				if (Array.isArray(currentValue)) {
					nextDynamic[filter.taxonomy] = [...new Set(currentValue.filter(Boolean))];
				} else if (typeof currentValue === 'string' && currentValue) {
					nextDynamic[filter.taxonomy] = currentValue
						.split(',')
						.map(value => value.trim())
						.filter(Boolean);
				} else {
					nextDynamic[filter.taxonomy] = [];
				}

				filterSearchTerms.value[filter.taxonomy] = '';
			});
			filters.value.dynamic = nextDynamic;
		};

		const normalizeFilterSlug = value => {
			if (typeof value !== 'string') {
				return '';
			}

			return value
				.trim()
				.toLowerCase()
				.replace(/\s+/g, '-')
				.replace(/[^a-z0-9_-]/g, '')
				.replace(/-+/g, '-');
		};

		const initializeFromUrl = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const seriesTaxonomy = `${postType.value}_series`;

			const viewParam = urlParams.get('view');
			if (viewParam === 'grid' || viewParam === 'list') {
				viewMode.value = viewParam;
			}

			// Legacy URL support: treat ?segment=<term> as the current post type's series taxonomy.
			const legacySegment = urlParams.get('segment');
			if (legacySegment && !urlParams.get(seriesTaxonomy)) {
				const normalizedSegment = normalizeFilterSlug(legacySegment);
				if (normalizedSegment) {
					urlParams.set(seriesTaxonomy, normalizedSegment);
				}
			}

			const pending = {};
			urlParams.forEach((value, key) => {
				if (key === 'view' || key === 'page' || !value) {
					return;
				}

				pending[key] = value;
			});

			pendingDynamicParams.value = pending;
		};

		const applyPendingDynamicParams = () => {
			const nextDynamic = { ...filters.value.dynamic };

			dynamicFilters.value.forEach(filter => {
				const pendingValue = pendingDynamicParams.value[filter.taxonomy];
				if (pendingValue) {
					nextDynamic[filter.taxonomy] = pendingValue
						.split(',')
						.map(value => normalizeFilterSlug(value))
						.filter(Boolean);
				}
			});

			filters.value.dynamic = nextDynamic;
		};

		const addFilterTerm = (taxonomy, slug) => {
			const selected = filters.value.dynamic?.[taxonomy] || [];
			if (selected.includes(slug)) {
				filterSearchTerms.value[taxonomy] = '';
				return;
			}

			filters.value.dynamic[taxonomy] = [...selected, slug];
			filterSearchTerms.value[taxonomy] = '';
			activeAutocompleteTaxonomy.value = taxonomy;
		};

		const removeFilterTerm = (taxonomy, slug) => {
			const selected = filters.value.dynamic?.[taxonomy] || [];
			filters.value.dynamic[taxonomy] = selected.filter(item => item !== slug);
		};

		const selectedTermsForTaxonomy = taxonomy => {
			const selectedSlugs = filters.value.dynamic?.[taxonomy] || [];
			const terms = dynamicFilterTerms.value[taxonomy] || [];

			return selectedSlugs
				.map(slug => terms.find(term => term.slug === slug) || { slug, name: slug })
				.filter(Boolean);
		};

		const autocompleteOptions = taxonomy => {
			const query = (filterSearchTerms.value[taxonomy] || '').trim().toLowerCase();
			const terms = dynamicFilterTerms.value[taxonomy] || [];
			const selectedSlugs = filters.value.dynamic?.[taxonomy] || [];

			return terms.filter(term => {
				return (
					(!query || term.name.toLowerCase().includes(query)) &&
					!selectedSlugs.includes(term.slug)
				);
			});
		};

		const isAutocompleteOpen = taxonomy => {
			return activeAutocompleteTaxonomy.value === taxonomy;
		};

		const openAutocomplete = taxonomy => {
			activeAutocompleteTaxonomy.value = taxonomy;
		};

		const closeAutocomplete = () => {
			setTimeout(() => {
				activeAutocompleteTaxonomy.value = null;
			}, 120);
		};

		const selectFirstSuggestion = taxonomy => {
			const suggestions = autocompleteOptions(taxonomy);
			if (suggestions.length > 0) {
				addFilterTerm(taxonomy, suggestions[0].slug);
			}
		};

		const clearAllFilters = () => {
			const dynamicObj = {};
			dynamicFilters.value.forEach(filter => {
				dynamicObj[filter.taxonomy] = [];
				filterSearchTerms.value[filter.taxonomy] = '';
			});

			filters.value.dynamic = dynamicObj;
		};

		const openProductModal = product => {
			selectedProduct.value = product;
			isProductModalOpen.value = true;
		};

		const closeProductModal = () => {
			isProductModalOpen.value = false;
			// Delay clearing the product to allow modal animation
			setTimeout(() => {
				selectedProduct.value = null;
			}, 300);
		};

		watch(
			() => filters.value.dynamic,
			() => {
				if (isHydratingFromUrl.value) {
					return;
				}

				updateUrlParams();
			},
			{ deep: true }
		);

		watch(viewMode, () => {
			if (isHydratingFromUrl.value) {
				return;
			}

			updateUrlParams();
		});

		const updateUrlParams = () => {
			const params = new URLSearchParams(window.location.search);
			const seriesTaxonomy = `${postType.value}_series`;

			if (viewMode.value !== 'grid') {
				params.set('view', viewMode.value);
			} else {
				params.delete('view');
			}

			// Remove legacy query param after hydration and keep only canonical taxonomy keys.
			params.delete('segment');
			params.delete(seriesTaxonomy);

			dynamicFilters.value.forEach(filter => {
				params.delete(filter.taxonomy);
			});

			if (filters.value.dynamic) {
				Object.entries(filters.value.dynamic).forEach(([key, values]) => {
					if (Array.isArray(values) && values.length > 0) {
						params.set(key, values.join(','));
					}
				});
			}

			const newUrl = params.toString()
				? `${window.location.pathname}?${params.toString()}${window.location.hash}`
				: window.location.pathname + window.location.hash;

			window.history.replaceState({}, '', newUrl);
		};

		const scrollToProducts = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const hasFilterParams = Array.from(urlParams.keys()).some(
				key => key !== 'page' && key !== 'view'
			);
			const shouldScroll = window.location.hash === '#products' || hasFilterParams;

			if (shouldScroll) {
				const productsSection = document.getElementById('products');
				if (productsSection) {
					setTimeout(() => {
						productsSection.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						});
					}, 300);
				}
			}
		};

		const handleWindowScroll = () => {
			showScrollTopButton.value = window.scrollY >= 2000;
		};

		const scrollToTop = () => {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		};

		onMounted(async () => {
			window.addEventListener('scroll', handleWindowScroll, { passive: true });
			handleWindowScroll();

			isHydratingFromUrl.value = true;
			initializeFromUrl();
			await fetchFilterConfig();
			initializeDynamicFilters();
			applyPendingDynamicParams();
			await fetchAllProducts();
			isHydratingFromUrl.value = false;
			updateUrlParams();
			scrollToProducts();
		});

		onUnmounted(() => {
			window.removeEventListener('scroll', handleWindowScroll);
		});

		return {
			seriesGroups,
			dynamicFilters,
			dynamicFilterTerms,
			loading,
			error,
			totalProducts,
			totalFilteredProducts,
			viewMode,
			filters,
			productTypeColor,
			isQuartzArchive,
			productGridColumnClass,
			selectedProduct,
			isProductModalOpen,
			clearAllFilters,
			filterSearchTerms,
			addFilterTerm,
			removeFilterTerm,
			selectedTermsForTaxonomy,
			autocompleteOptions,
			isAutocompleteOpen,
			openAutocomplete,
			closeAutocomplete,
			selectFirstSuggestion,
			openProductModal,
			closeProductModal,
			typeImageUrl,
			showScrollTopButton,
			scrollToTop,
		};
	},
};
</script>

<style scoped>
/* Component styles will be in archive_listing.scss */
</style>
